import { apiFetch } from "../api.js";
import type {
  FireAssumptions,
  HouseholdInvitation,
  HouseholdMember,
  InvitationStatus,
} from "./types.js";

export function listMemberships(
  householdId: string,
): Promise<{ memberships: HouseholdMember[] }> {
  return apiFetch<{ memberships: HouseholdMember[] }>(
    `/v1/households/${encodeURIComponent(householdId)}/memberships`,
  );
}

export function listInvitations(
  householdId: string,
  status?: InvitationStatus,
): Promise<{ invitations: HouseholdInvitation[] }> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiFetch<{ invitations: HouseholdInvitation[] }>(
    `/v1/households/${encodeURIComponent(householdId)}/invitations${qs}`,
  );
}

/**
 * Partial-merge update of the per-household FIRE assumption block.
 * Send `null` on any override field (e.g. annual_spend_override_cents,
 * barista_annual_income_cents, withdrawal_strategy, bond_tent_*) to
 * clear it; send undefined / omit to leave it untouched.
 *
 * Phase 6 Session 1 (base block) + Phase 6 Session 3 (withdrawal
 * strategy + spending smile + bond tent).
 */
export type WithdrawalStrategy =
  | "bengen_constant"
  | "guyton_klinger"
  | "vpw"
  | "cape_based"
  | "dynamic_band";

export type SpendingSmileCurve = "blanchett_2014";

export type VpwTable = "bogleheads_2020";

export type UpdateFireAssumptionsInput = {
  withdrawal_rate_pct?: number;
  expected_return_rate_pct?: number;
  lean_spend_multiplier?: number;
  fat_spend_multiplier?: number;
  birth_year_primary?: number | null;
  retirement_target_age?: number;
  birth_year_spouse?: number | null;
  social_security_pia_spouse_cents?: number | null;
  social_security_claim_age_spouse?: number | null;
  pre_medicare_healthcare_annual_cents?: number | null;
  annual_spend_override_cents?: number | null;
  barista_annual_income_cents?: number | null;

  // Phase 6 Session 3
  withdrawal_strategy?: WithdrawalStrategy | null;
  gk_floor_pct?: number | null;
  gk_ceiling_pct?: number | null;
  gk_cut_threshold_pct?: number | null;
  gk_raise_threshold_pct?: number | null;
  vpw_table?: VpwTable | null;
  cape_floor_pct?: number | null;
  cape_anchor?: number | null;
  dynamic_band_pct?: number | null;
  spending_smile_enabled?: boolean | null;
  spending_smile_curve?: SpendingSmileCurve | null;
  bond_tent_enabled?: boolean | null;
  bond_tent_ramp_start_years?: number | null;
  bond_tent_ramp_end_years?: number | null;
  bond_tent_floor_pct?: number | null;
  bond_tent_ceiling_pct?: number | null;

  // Phase 6 Session 4 — Social Security + geographic arbitrage
  social_security_pia_cents?: number | null;
  social_security_claim_age?: number | null;
  current_city_code?: string | null;
};

export function updateFireAssumptions(
  householdId: string,
  input: UpdateFireAssumptionsInput,
): Promise<{ fire_assumptions: FireAssumptions }> {
  return apiFetch<{ fire_assumptions: FireAssumptions }>(
    `/v1/households/${encodeURIComponent(householdId)}/fire-assumptions`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
}

/**
 * Phase 13 — top-level household profile update. Today this carries
 * `wedding_date` (powers the daily AnniversaryCelebrationFn cron) and
 * `name`. FIRE assumptions remain on the dedicated endpoint above.
 */
export interface UpdateHouseholdInput {
  name?: string;
  wedding_date?: string | null;
}

export function updateHousehold(
  householdId: string,
  input: UpdateHouseholdInput,
): Promise<{
  household_id: string;
  name?: string;
  wedding_date: string | null;
}> {
  return apiFetch<{
    household_id: string;
    name?: string;
    wedding_date: string | null;
  }>(`/v1/households/${encodeURIComponent(householdId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}
